import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, Footprints, Droplet, Plus, Play, Apple, Camera, Sparkles, Trophy } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import { toast } from "sonner";
import { api, useContent, awardPoints } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const QUOTES = [
  "Discipline is choosing between what you want now and what you want most.",
  "You don't have to be extreme, just consistent.",
  "Small steps every day add up to big results.",
  "The body achieves what the mind believes.",
  "Fall in love with the process and the results will come.",
];

function Ring({ value, max, label, unit, icon: Icon, onAdd, testid }) {
  const pct = Math.min(1, (value || 0) / max);
  const r = 34, c = 2 * Math.PI * r;
  return (
    <div className="border border-border rounded-xl bg-card p-5 flex flex-col items-center" data-testid={testid}>
      <div className="relative w-24 h-24">
        <svg className="w-24 h-24 -rotate-90" viewBox="0 0 80 80">
          <circle cx="40" cy="40" r={r} fill="none" strokeWidth="7" className="stroke-secondary" />
          <motion.circle cx="40" cy="40" r={r} fill="none" strokeWidth="7" strokeLinecap="round" className="stroke-primary"
            strokeDasharray={c} initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: c - c * pct }} transition={{ duration: 1, ease: "easeOut" }} />
        </svg>
        <div className="absolute inset-0 grid place-items-center"><Icon size={22} className="text-primary" /></div>
      </div>
      <div className="mt-3 text-center">
        <div className="font-display font-800 text-2xl leading-none">{value || 0}<span className="text-sm text-muted-foreground"> /{max}{unit}</span></div>
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground mt-1">{label}</div>
      </div>
      {onAdd && <button onClick={onAdd} data-testid={`${testid}-add`} className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline"><Plus size={13} /> Add</button>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { data: workouts } = useContent("workouts");
  const { data: challenges } = useContent("challenges");
  const [today, setToday] = useState({ water_ml: 0, steps: 0, calories: 0, workout_done: false });
  const [streak, setStreak] = useState({ streak: 0, total_workouts: 0 });
  const [progress, setProgress] = useState([]);
  const quote = QUOTES[new Date().getDate() % QUOTES.length];
  const todayWorkout = workouts[new Date().getDate() % Math.max(1, workouts.length)];

  const load = () => {
    api.get("/me/today").then((r) => setToday(r.data)).catch(() => {});
    api.get("/me/streak").then((r) => setStreak(r.data)).catch(() => {});
    api.get("/me/progress").then((r) => setProgress(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const log = async (fields, msg) => {
    try { const { data } = await api.post("/me/log", fields); setToday(data); if (msg) toast.success(msg); api.get("/me/streak").then((r) => setStreak(r.data)); }
    catch { toast.error("Could not update."); }
  };

  const chartData = progress.filter((p) => p.weight != null).map((p) => ({ date: p.date.slice(5), weight: p.weight }));

  return (
    <div data-testid="dashboard-page" className="px-4 md:px-8 pb-16 pt-2 md:pt-4">
      {/* Greeting */}
      <div className="mb-6">
        <h1 className="font-display font-900 uppercase text-4xl md:text-5xl tracking-tight">Hi {(user.name || "").split(" ")[0] || "there"} 👋</h1>
        <p className="text-muted-foreground mt-1 italic">"{quote}"</p>
      </div>

      {/* Top grid */}
      <div className="grid gap-4 lg:grid-cols-3">
        {/* Workout of day */}
        <div className="lg:col-span-2 relative overflow-hidden rounded-xl border border-border min-h-[220px] flex items-end" data-testid="workout-of-day">
          {todayWorkout && <img src={todayWorkout.image} alt="" className="absolute inset-0 w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
          <div className="relative p-6 text-white w-full">
            <span className="text-[11px] uppercase tracking-[0.2em] text-primary font-bold">Today's Workout</span>
            <h2 className="font-display font-800 uppercase text-3xl md:text-4xl tracking-tight mt-1">{todayWorkout?.title || "Rest Day"}</h2>
            {todayWorkout && <div className="flex flex-wrap gap-4 text-sm text-white/80 mt-2"><span>{todayWorkout.duration}</span><span>{todayWorkout.difficulty}</span><span>{todayWorkout.calories} kcal</span></div>}
            <div className="flex gap-3 mt-4">
              {todayWorkout && <Link to={`/app/workouts/${todayWorkout.id}`} data-testid="start-workout" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full text-xs uppercase tracking-[0.12em] font-bold"><Play size={14} /> Start</Link>}
              <button onClick={() => log({ workout_done: true, calories: (today.calories || 0) + (todayWorkout?.calories || 0) }, "Workout logged! +10 pts")} data-testid="mark-done" disabled={today.workout_done}
                className="inline-flex items-center gap-2 bg-white/15 backdrop-blur border border-white/40 px-6 py-3 rounded-full text-xs uppercase tracking-[0.12em] font-bold disabled:opacity-60">
                {today.workout_done ? "Done ✓" : "Mark Complete"}
              </button>
            </div>
          </div>
        </div>

        {/* Streak */}
        <div className="rounded-xl border border-border bg-card p-6 flex flex-col justify-center items-center text-center" data-testid="streak-card">
          <Flame size={40} className="text-primary" />
          <div className="font-display font-900 text-6xl leading-none mt-2">{streak.streak}</div>
          <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mt-1">Day Streak</div>
          <div className="text-sm text-muted-foreground mt-3">{streak.total_workouts} workouts done · {user.points} pts</div>
        </div>
      </div>

      {/* Trackers */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <Ring testid="tracker-water" value={today.water_ml} max={3000} unit="ml" label="Water" icon={Droplet} onAdd={async () => { const nv = (today.water_ml || 0) + 250; await log({ water_ml: nv }); if (nv >= 3000) { const p = await awardPoints("water_goal"); if (p) toast.success(`Water goal hit! +${p} pts`); } }} />
        <Ring testid="tracker-steps" value={today.steps} max={10000} unit="" label="Steps" icon={Footprints} onAdd={() => log({ steps: (today.steps || 0) + 1000 })} />
        <Ring testid="tracker-calories" value={today.calories} max={600} unit="" label="Calories" icon={Flame} onAdd={() => log({ calories: (today.calories || 0) + 100 })} />
        <div className="border border-border rounded-xl bg-card p-5 flex flex-col justify-center">
          <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-3">Quick Actions</p>
          <div className="grid grid-cols-2 gap-2">
            <Link to="/app/nutrition" className="flex items-center gap-1.5 text-xs font-bold border border-border rounded-lg px-3 py-2.5 hover:border-primary transition-colors"><Apple size={14} className="text-primary" /> Diet</Link>
            <Link to="/app/progress" className="flex items-center gap-1.5 text-xs font-bold border border-border rounded-lg px-3 py-2.5 hover:border-primary transition-colors"><Camera size={14} className="text-primary" /> Photos</Link>
            <Link to="/app/coach" className="flex items-center gap-1.5 text-xs font-bold border border-border rounded-lg px-3 py-2.5 hover:border-primary transition-colors"><Sparkles size={14} className="text-primary" /> Coach</Link>
            <Link to="/app/challenges" className="flex items-center gap-1.5 text-xs font-bold border border-border rounded-lg px-3 py-2.5 hover:border-primary transition-colors"><Trophy size={14} className="text-primary" /> Challenge</Link>
          </div>
        </div>
      </div>

      {/* Weight chart + challenges */}
      <div className="grid gap-4 lg:grid-cols-3 mt-4">
        <div className="lg:col-span-2 border border-border rounded-xl bg-card p-6" data-testid="weight-chart">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-700 uppercase text-2xl tracking-tight">Weight Progress</h3>
            <Link to="/app/progress" className="text-xs uppercase tracking-wide font-bold text-primary">Log +</Link>
          </div>
          {chartData.length > 1 ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData} margin={{ left: -20, right: 10, top: 10 }}>
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} domain={["auto", "auto"]} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--foreground))" }} />
                <Line type="monotone" dataKey="weight" stroke="hsl(var(--primary))" strokeWidth={3} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] grid place-items-center text-center text-muted-foreground text-sm">Log your weight in Progress to see your trend here.</div>
          )}
        </div>
        <div className="border border-border rounded-xl bg-card p-6" data-testid="upcoming-challenges">
          <h3 className="font-display font-700 uppercase text-2xl tracking-tight mb-4">Challenges</h3>
          <div className="space-y-3">
            {challenges.slice(0, 3).map((c) => (
              <Link key={c.id} to="/app/challenges" className="flex items-center gap-3 group">
                <img src={c.image} alt="" className="w-12 h-12 rounded-lg object-cover" />
                <div className="min-w-0"><div className="font-bold text-sm truncate group-hover:text-primary transition-colors">{c.title}</div><div className="text-xs text-muted-foreground">{c.days} days · {c.difficulty}</div></div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
